import React, { useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, User, Users, Landmark } from 'lucide-react';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { SegmentedToggle } from '../../components/ui/segmented-toggle';
import { toast } from 'sonner';

/**
 * SCREEN 3: Membership Payment (FINAL STEP)
 * 
 * Purpose: Complete the membership signup with payment.
 * - Specific to the selected membership
 * - Pricing, billing toggle, promo code
 * - No re-explaining philosophy
 */

const MEMBERSHIP_PRICING = {
  individual: {
    title: 'Individual Membership',
    icon: User,
    color: '#7A8F5B',
    options: [
      { id: 'open', name: 'Open Member', priceMonthly: 0, priceYearly: 0, isFree: true },
      { id: 'personal', name: 'Personal Member', priceMonthly: 4.99, priceYearly: 49.99, recommended: true },
    ],
  },
  family: {
    title: 'Family Membership',
    icon: Users,
    color: '#D98A3A',
    options: [
      { id: 'family', name: 'Family Plan', priceMonthly: 7.99, priceYearly: 79.99, recommended: true },
    ],
  },
  commons: {
    title: 'Commons — make a group last',
    icon: Landmark,
    color: '#8B6A4F',
    options: [
      { id: 'commons', name: 'Commons', priceMonthly: 20.00, priceYearly: 200.00, recommended: true, requiresBase: true },
    ],
    note: 'Requires an active Personal Membership ($49.99/year)',
  },
};

export default function MembershipPayment() {
  const navigate = useNavigate();
  const { type } = useParams();
  const { user } = useContext(AuthContext);
  
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [selectedOption, setSelectedOption] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [loading, setLoading] = useState(false);
  
  const pricing = MEMBERSHIP_PRICING[type];
  
  // Auto-select recommended option - must be before any conditional returns
  React.useEffect(() => {
    if (!pricing) return;
    const recommended = pricing.options.find(o => o.recommended);
    if (recommended && !selectedOption) {
      setSelectedOption(recommended.id);
    } else if (pricing.options.length === 1 && !selectedOption) {
      setSelectedOption(pricing.options[0].id);
    }
  }, [pricing, selectedOption]);

  if (!pricing) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="text-center">
          <p style={{ color: 'var(--fy-text-primary)' }}>Membership type not found.</p>
          <Button onClick={() => navigate('/membership')} className="mt-4">
            Go back
          </Button>
        </div>
      </div>
    );
  }

  const Icon = pricing.icon;
  
  const getPrice = (option) => {
    if (option.isFree) return 'Free';
    const price = billingCycle === 'yearly' ? option.priceYearly : option.priceMonthly;
    const period = billingCycle === 'yearly' ? '/year' : '/month';
    return `$${price.toFixed(2)}${period}`;
  };

  const handleSubmit = async () => {
    if (!user) {
      // Store selection and redirect to auth
      sessionStorage.setItem('lk_membership_selection', JSON.stringify({
        type,
        option: selectedOption,
        billingCycle,
        promoCode,
      }));
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      // For free tier, just update membership
      const selected = pricing.options.find(o => o.id === selectedOption);
      if (selected?.isFree) {
        toast.success('Welcome to LikeKind!');
        navigate('/');
        return;
      }

      // For paid tiers, redirect to payment processing
      toast.success('Redirecting to payment...');
      // In production, this would integrate with Stripe
      navigate('/');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selected = pricing.options.find(o => o.id === selectedOption);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="flex items-center p-4">
        <button
          onClick={() => navigate(`/membership/${type}`)}
          className="p-2 rounded-lg"
          style={{ color: 'var(--fy-text-secondary)' }}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </header>

      {/* Content */}
      <main className="px-6 pb-12">
        <div className="max-w-md mx-auto">
          {/* Title */}
          <div className="text-center mb-6">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
              style={{ backgroundColor: `${pricing.color}15` }}
            >
              <Icon className="w-6 h-6" style={{ color: pricing.color }} />
            </div>
            <h1 
              className="text-xl font-bold"
              style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}
            >
              {pricing.title}
            </h1>
          </div>

          {/* Billing toggle (only if paid options exist) */}
          {pricing.options.some(o => !o.isFree) && (
            <div className="flex justify-center mb-6">
              <SegmentedToggle
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'yearly', label: 'Yearly', badge: 'Save 17%' },
                ]}
                value={billingCycle}
                onChange={setBillingCycle}
                variant="billing"
              />
            </div>
          )}

          {/* Options */}
          <div className="space-y-3 mb-6">
            {pricing.options.map((option) => (
              <button
                key={option.id}
                onClick={() => setSelectedOption(option.id)}
                className="w-full p-4 rounded-xl text-left transition-all"
                style={{ 
                  backgroundColor: selectedOption === option.id ? `${pricing.color}10` : 'var(--fy-surface-card)',
                  border: selectedOption === option.id ? `2px solid ${pricing.color}` : '2px solid var(--fy-border-subtle)',
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                      {option.name}
                      {option.recommended && (
                        <span 
                          className="ml-2 text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: pricing.color, color: '#FFFFFF' }}
                        >
                          Recommended
                        </span>
                      )}
                    </p>
                    {option.requiresBase && (
                      <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
                        + Personal Member base
                      </p>
                    )}
                  </div>
                  <p className="font-bold text-lg" style={{ color: pricing.color }}>
                    {getPrice(option)}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Note */}
          {pricing.note && (
            <p 
              className="text-center text-xs mb-6"
              style={{ color: 'var(--fy-text-disabled)' }}
            >
              {pricing.note}
            </p>
          )}

          {/* Promo code */}
          <div className="mb-6">
            <label className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Promo code <span style={{ color: 'var(--fy-text-disabled)' }}>(optional)</span>
            </label>
            <Input
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="e.g., LK-FAM-01A9"
              className="mt-1 h-12 rounded-xl uppercase"
              style={{ border: '1px solid var(--fy-border-subtle)' }}
            />
          </div>

          {/* Total */}
          {selected && !selected.isFree && (
            <div 
              className="p-4 rounded-xl mb-6"
              style={{ backgroundColor: 'var(--fy-surface-muted)' }}
            >
              <div className="flex justify-between items-center">
                <span style={{ color: 'var(--fy-text-secondary)' }}>Total</span>
                <span className="font-bold text-xl" style={{ color: 'var(--fy-text-primary)' }}>
                  {getPrice(selected)}
                </span>
              </div>
              {billingCycle === 'yearly' && (
                <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
                  Billed annually
                </p>
              )}
            </div>
          )}

          {/* Submit button */}
          <Button
            onClick={handleSubmit}
            disabled={!selectedOption || loading}
            className="w-full h-12 rounded-full font-bold"
            style={{ backgroundColor: pricing.color, color: '#FFFFFF' }}
            data-testid="complete-signup"
          >
            {loading ? 'Processing...' : user ? 'Complete Signup' : 'Continue to Create Account'}
          </Button>

          {/* Sign in link for unauthenticated */}
          {!user && (
            <p className="text-center text-sm mt-4" style={{ color: 'var(--fy-text-secondary)' }}>
              Already have an account?{' '}
              <button
                onClick={() => navigate('/auth')}
                className="font-medium"
                style={{ color: pricing.color }}
              >
                Sign in
              </button>
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
